import axios from 'axios';
import { auth, getCurrentUser } from './firebase';

const baseURL = `/api/broker`;

const api = axios.create({
  baseURL,
  withCredentials: true
});

api.interceptors.request.use(async config => {
  const user = await getCurrentUser();

  if (!user) return config;

  const tokenResult = await user.getIdTokenResult();
  config.headers.Authorization =  `Bearer ${tokenResult.token}`;

  return config;
});

const createAccreditation = async (payload = {
  projects: null,
  name: null,
  email: null,
  mobilePhone: null,
  creci: null,
  tshirtSize: null,
  realState: null,
  realStateDocument: null,
  realStateCreci: null,
  realStateResponsibleName: null,
  realStateResponsibleMobilePhone: null,
  realStateResponsibleEmail: null,
}) => await api.post(`/accreditations`, payload);

const createProposal = async (payload = {
  projectId: null,
  identifier: null,
  area: null,
  areaPrice: null,
  finalPrice: null,
  kind: null,
  fundingEntry: null,
  fundingEntryPercent: null,
  outstandingBalance: null,
  numberOfInstallments: null,
  rate: null,
  indexingRate: null,
  fundingInstallmentValue: null,
  fundingIntermediariesData: null,
  clientName: null,
  clientEmail: null,
  clientMobilePhone: null,
  clientDocument: null,
  observation: null,
  trustConfirmation: null
}) => await api.post(`/proposals`, payload);

const createSimulation = async (payload = {
  projectId: null,
  identifier: null,
  area: null,
  areaPrice: null,
  finalPrice: null,
  kind: null,
  fundingEntry: null,
  fundingEntryPercent: null,
  outstandingBalance: null,
  numberOfInstallments: null,
  rate: null,
  indexingRate: null,
  fundingInstallmentValue: null,
  fundingIntermediariesData: null
}) => await api.post(`/simulations`, payload);

const generateProjectImageUrl = (projectId, projectImageId) => `${baseURL}/projects/${projectId}/images/${projectImageId}`
const generateProjectMapUrl = (projectId, pageDataName) => `${baseURL}/projects/${projectId}/map`
const generateProjectPageDataUrl = (projectId, pageDataName) => `${baseURL}/projects/${projectId}/pageData/${pageDataName}`
const getProfile = async () => await api.get(`/profile`);
const getProject = async (id) => await api.get(`/projects/${id}`);
const getProjectLot = async (projectId, lotId) => await api.get(`/projects/${projectId}/lots/${lotId}`);
const getProjectLotFilterOptions = async (projectId) => await api.get(`/projects/${projectId}/lots/filter-options`);

// Rota pública do backend (sem login): acesso controlado pelo token do link.
const getPublicConversation = async (threadId, token) =>
  await axios.get(`/api/public/mastra/threads/${encodeURIComponent(threadId)}/messages`, { params: { token } });

const listProjects = async () => await api.get('/projects');

const listProjectLots = async (projectId, filters = {status: null, maximumArea: null, maximumPrice: null, identifierStartsWith: null}) => {
  const params = Object.keys(filters).filter(f => filters[f] !== null).map(f => `${f}=${filters[f]}`);
  return await api.get(`/projects/${projectId}/lots?${params.join('&')}`);
}
const listProjectPageDatas = async (projectId) => await api.get(`/projects/${projectId}/pageData`);
const getProjectPageDataFull = async (projectId) => await api.get(`/projects/${projectId}/pageData/full`);

const updateProfile = async (payload = {
  name: null,
  email: null,
  addressZipcode: null,
  addressStreet: null,
  addressNumber: null,
  addressComplement: null,
  addressDistrict: null,
  addressCity: null,
  addressState: null,
  mobilePhone: null,
  landlinePhone: null
}) => await api.post(`/profile`, payload);

const signInWithEmail = async (email, password) => auth().signInWithEmailAndPassword(email, password);
const signOut = async () => await auth().signOut();

const signUpWithEmail = async (payload = {
  name: null,
  email: null,
  password: null,
  addressZipcode: null,
  addressStreet: null,
  addressNumber: null,
  addressComplement: null,
  addressDistrict: null,
  addressCity: null,
  addressState: null,
  mobilePhone: null,
  landlinePhone: null
}) => await api.post(`/signup`, payload);

const signUpWithSocial = async (payload = {
  name: null,
  email: null,
  addressZipcode: null,
  addressStreet: null,
  addressNumber: null,
  addressComplement: null,
  addressDistrict: null,
  addressCity: null,
  addressState: null,
  mobilePhone: null,
  landlinePhone: null,
}) => await api.post(`/signup/complete`, payload);

export default {
  baseURL,
  createAccreditation,
  createProposal,
  createSimulation,
  generateProjectImageUrl,
  generateProjectMapUrl,
  generateProjectPageDataUrl,
  getProfile,
  getProject,
  getProjectLot,
  getProjectLotFilterOptions,
  getProjectPageDataFull,
  getPublicConversation,
  listProjects,
  listProjectLots,
  listProjectPageDatas,
  signInWithEmail,
  signOut,
  signUpWithEmail,
  signUpWithSocial,
  updateProfile,
};
